import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫画网',
    description: '韩漫画网是专业的韩漫免费在线阅读平台，提供下拉式漫画大全和流畅的韩国漫画在线服务。',
    url: 'https://hanmanhua.com/about',
    about: {
      '@type': 'Organization',
      name: '韩漫画网',
      foundingDate: '2024',
      description: '韩漫画网致力于为全球华语读者提供优质的韩漫在线免费阅读体验。',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>

            <article>
              {/* Brand Introduction */}
              <section className="mb-16">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  关于韩漫画网
                </h1>
                <p className="text-sm text-foreground-500 mb-8">韩漫免费在线阅读平台，下拉式漫画大全</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div>
                    <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      韩漫画网成立于2024年，是专注于韩国漫画在线阅读的专业平台。我们致力于为全球华语读者提供优质的韩漫免费阅读服务，汇集海量热门韩漫资源，支持下拉式连贯阅读体验。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      平台涵盖恋爱、奇幻、动作、校园、悬疑、古风、剧情、惊悚、武侠等多种题材，每日实时更新最新章节，让漫画爱好者随时随地畅享韩国漫画带来的阅读乐趣。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      我们坚信，好的漫画应该被更多人看到。韩漫画网以用户为中心，不断优化阅读体验，打造简洁流畅的漫画阅读环境，让每一位读者都能轻松找到并享受自己喜爱的韩漫作品。
                    </p>
                  </div>
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20reading%20space%20with%20warm%20amber%20lighting%2C%20digital%20tablet%20displaying%20manhwa%20style%20illustration%2C%20cozy%20atmosphere%2C%20bookshelf%20background%2C%20clean%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20environment%20with%20soft%20natural%20light&width=800&height=500&seq=about-brand-01&orientation=landscape"
                      alt="韩漫画网品牌"
                      title="韩漫画网 - 品牌介绍"
                      className="w-full object-cover rounded-xl"
                    />
                  </div>
                </div>
              </section>

              {/* Reputation */}
              <section className="mb-16 py-12 px-6 md:px-10 rounded-2xl bg-background-100">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviews.map((review, index) => (
                    <div key={index} className="p-5 rounded-xl bg-background-50">
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-fill text-sm ${i < review.rating ? 'text-accent-500' : 'text-background-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.content}</p>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                          <span className="text-xs font-bold text-secondary-700">{review.name[0]}</span>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-foreground-700">{review.name}</div>
                          <div className="text-xs text-foreground-400">{review.date}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  常见问题
                </h2>
                <div className="flex flex-col gap-3">
                  {faqs.map((faq, index) => (
                    <FAQItem key={index} question={faq.question} answer={faq.answer} defaultOpen={index === 0} />
                  ))}
                </div>
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      mainEntity: faqs.map((faq) => ({
                        '@type': 'Question',
                        name: faq.question,
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: faq.answer,
                        },
                      })),
                    }),
                  }}
                />
              </section>

              {/* Contact */}
              <section className="py-12 px-6 md:px-10 rounded-2xl bg-background-100">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mb-8 text-center">
                  有任何问题或建议，欢迎随时联系我们
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <div className="flex flex-col gap-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                          <i className="ri-mail-line text-primary-500 text-lg"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                          <p className="text-sm text-foreground-600">contact@hanmanhua.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                          <i className="ri-map-pin-line text-accent-500 text-lg"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                          <p className="text-sm text-foreground-600">上海市浦东新区张江高科技园区</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                          <i className="ri-time-line text-secondary-500 text-lg"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                          <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <ContactForm />
                </div>
              </section>
            </article>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}

function FAQItem({ question, answer, defaultOpen = false }: { question: string; answer: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-background-200 bg-background-50 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
      >
        <h3 className="text-sm font-semibold text-foreground-900 pr-4">{question}</h3>
        <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-foreground-500"></i>
        </div>
      </button>
      {open && (
        <div className="px-4 md:px-5 pb-4 md:pb-5">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pc2t2qs38sj6jc7lj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.meta &&
          typeof ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown>)?.message === 'string'
          ? ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown>).message as string
          : '';
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  if (formStatus === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 rounded-xl bg-background-50">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 mb-4">
          <i className="ri-check-line text-3xl text-primary-500"></i>
        </div>
        <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">感谢您的留言！</h3>
        <p className="text-sm text-foreground-500">我们已收到您的信息，会尽快回复您。</p>
        <button
          onClick={() => setFormStatus('idle')}
          className="mt-4 px-5 py-2 rounded-full text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          发送新消息
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form="">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              placeholder="请输入您的邮箱"
            />
          </div>
        </div>
        <div>
          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
          <input
            id="contact-subject"
            name="subject"
            type="text"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
            placeholder="请输入主题"
          />
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={4}
            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
            placeholder="请输入您的留言（最多500字）"
          ></textarea>
          <p className="text-xs text-foreground-400 mt-1">最多500字</p>
        </div>
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        {formStatus === 'error' && formError && (
          <p className="text-sm text-red-500">{formError}</p>
        )}
        <button
          type="submit"
          className="w-full px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          发送消息
        </button>
      </div>
    </form>
  );
}

const reviews = [
  {
    name: '小雨',
    rating: 5,
    content: '韩漫画网真的太棒了！下拉式阅读体验非常流畅，资源也特别丰富，每天都有更新，是我用过最好的韩漫阅读平台。',
    date: '2026-07-15',
  },
  {
    name: '星辰大海',
    rating: 5,
    content: '一直在找能免费看韩漫的网站，终于找到了！分类很全，恋爱、奇幻、动作都有，界面也很清爽，强烈推荐。',
    date: '2026-07-20',
  },
  {
    name: '漫画控',
    rating: 4,
    content: '漫画更新的速度很快，基本同步韩国连载。下拉阅读的方式也很舒服，不用频繁翻页。希望能增加夜间模式。',
    date: '2026-07-28',
  },
  {
    name: '追漫达人',
    rating: 5,
    content: '作为韩漫重度爱好者，这个网站简直是宝藏！收藏了好多喜欢的作品，每天打开看看有没有更新已经成为习惯了。',
    date: '2026-08-01',
  },
  {
    name: '叶子',
    rating: 5,
    content: '朋友推荐来的，一用就爱上了。页面设计简洁大方，漫画画质清晰，加载速度也快，看漫画的体验非常好。',
    date: '2026-08-02',
  },
  {
    name: '流浪的猫',
    rating: 4,
    content: '资源真的很全，好多小众韩漫都能找到。唯一的建议是希望增加评论功能，想和其他读者交流追漫心得。',
    date: '2026-08-04',
  },
];

const faqs = [
  {
    question: '韩漫画网的漫画都是免费的吗？',
    answer: '是的，韩漫画网所有漫画均提供免费在线阅读服务。我们致力于让每一位韩漫爱好者都能无障碍地享受阅读乐趣。所有漫画资源均来自互联网公开渠道，版权归原作者所有。',
  },
  {
    question: '什么是下拉式阅读？',
    answer: '下拉式阅读是一种连续滚动的阅读方式，读者只需向下滑动页面即可连续浏览漫画内容，无需频繁点击翻页。这种方式更加流畅自然，特别适合在手机和平板等移动设备上阅读长篇漫画，让您沉浸在漫画世界中不受打断。',
  },
  {
    question: '漫画多久更新一次？',
    answer: '韩漫画网每日更新，我们会第一时间同步韩国最新的漫画连载。连载中的作品通常每周更新一话，具体更新时间因作品而异。您可以在漫画详情页查看最近更新时间，也可以关注我们的首页"最新更新"板块了解最新动态。',
  },
  {
    question: '如何在手机上阅读韩漫？',
    answer: '韩漫画网采用响应式设计，完美适配手机、平板和电脑。您可以直接在手机浏览器中访问我们的网站，无需下载任何应用。页面会自动适配您的屏幕尺寸，提供最佳的下拉式阅读体验，让您随时随地畅享韩漫。',
  },
  {
    question: '漫画画质如何？',
    answer: '我们提供的漫画均为高清画质，确保每一个细节都清晰可见。我们重视阅读体验，采用先进的图片加载技术，在保证画质的同时确保加载速度快，让您享受流畅的高清阅读体验。',
  },
];